<script setup lang="ts">
import type {
  VideoGalleryDetailDTO,
  VideoGalleryDTO,
} from '~/shared/api/client/galleries/galleries.dto';
import type { VideoDTO } from '~/shared/api/client/video/video.dto';
import type { MediaItem } from '~/shared/ui/adaptive-media-grid/types';
import AdaptiveMediaGrid from '~/shared/ui/adaptive-media-grid/AdaptiveMediaGrid.vue';
import { EVENT_MEDIA_MODES } from '~/shared/lib/events-routes.const';
import EventsGalleryDetailLayout from './EventsGalleryDetailLayout.vue';
import { useEventGalleryDetail } from '../lib/useEventGalleryDetail';

const {
  config,
  gallery,
  galleryId,
  hasGallery,
  isPending,
  pageUrl,
  relatedGalleries,
  title,
} = useEventGalleryDetail<VideoGalleryDetailDTO, VideoGalleryDTO>(
  EVENT_MEDIA_MODES.videos,
);

const activeVideo = ref<VideoDTO | null>(null);

watch(galleryId, () => {
  activeVideo.value = null;
});

const videos = computed(() =>
  (gallery.value?.videos ?? []).filter((item) => Boolean(item.video_url)),
);

const videoItems = computed<MediaItem<VideoDTO>[]>(() =>
  videos.value.map((video) => ({
    id: video.id,
    src: video.first_frame?.file_url ?? null,
    alt: getVideoTitle(video),
    payload: video,
  })),
);

function getVideoTitle(video: VideoDTO): string {
  return video.description || title.value || 'Видео мероприятия';
}

function openVideoItem(_: MediaItem, index: number): void {
  const video = videoItems.value[index]?.payload;

  if (video) {
    activeVideo.value = video;
  }
}

function closeVideoModal(): void {
  activeVideo.value = null;
}
</script>

<template>
  <div :class="$style.root">
    <EventsGalleryDetailLayout
      :mode="EVENT_MEDIA_MODES.videos"
      :title="title"
      :page-url="pageUrl"
      :is-pending="isPending"
      :has-gallery="hasGallery"
      :related-galleries="relatedGalleries"
    >
      <AdaptiveMediaGrid
        v-if="videoItems.length > 0"
        :items="videoItems"
        @item-click="openVideoItem"
      >
        <template #placeholder>
          <span :class="$style.placeholder" />
        </template>
        <template #overlay>
          <span :class="$style.playIcon">
            <Icon name="my-icon:i-play" :size="20" />
          </span>
        </template>
      </AdaptiveMediaGrid>

      <div v-else :class="$style.emptyState">
        <span :class="$style.stateText">{{ config.emptyText }}</span>
      </div>
    </EventsGalleryDetailLayout>

    <ModalVideo
      v-if="activeVideo?.video_url"
      :is-visible="Boolean(activeVideo)"
      :iframe="activeVideo.video_url"
      :title="getVideoTitle(activeVideo)"
      @close="closeVideoModal"
    />
  </div>
</template>

<style module lang="scss">
.root {
  min-height: 100%;
}

.placeholder {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    var(--color-gray-line) 0%,
    var(--color-gray-bg) 100%
  );
}

.playIcon {
  position: absolute;
  top: 50%;
  left: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background-color: var(--color-red-1);
  color: var(--color-white);
  transform: translate(-50%, -50%);
  transition: transform 0.2s;

  path {
    fill: var(--color-white);
  }
}

:global(button:hover) .playIcon {
  transform: translate(-50%, -50%) scale(1.08);
}

.emptyState {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 240px;
}

.stateText {
  @include text_body1;
  margin: 0;
  color: var(--color-gray-text);
}
</style>
